import { useMutation, useQuery } from '@tanstack/react-query';
import { Globe, Loader2, Send, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { AssistantAnswer } from '../lib/types';

type Turn = { question: string; answer: AssistantAnswer | null; error?: string };

/**
 * The assistant, reachable from the top bar on every screen.
 *
 * It answers about the business the person asking can actually see. That is
 * not a courtesy — the service applies the same capability checks and row
 * filters as the screens, so a technician gets their own jobs and a refusal
 * with a reason if they ask about the roster. An assistant that read
 * everything and filtered afterwards would be the widest hole in the
 * application: one endpoint that bypasses every permission, wrapped in a chat
 * box.
 *
 * The numbers come from the database, not from a model. A model, when one is
 * configured, only helps decide what was being asked. So a bad answer is a
 * visibly wrong question rather than an invented figure that reads perfectly
 * and is false — which is the failure mode that would actually cost money.
 *
 * Hidden entirely when an admin has switched it off, and the API refuses too:
 * a hidden button is not a switch.
 */
export function Assistant() {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  const status = useQuery({ queryKey: ['assistant'], queryFn: api.assistantStatus });

  const ask = useMutation({
    mutationFn: (text: string) => api.assistantAsk(text),
    onMutate: (text) => setTurns((current) => [...current, { question: text, answer: null }]),
    onSuccess: (answer) =>
      setTurns((current) =>
        current.map((turn, index) => (index === current.length - 1 ? { ...turn, answer } : turn)),
      ),
    onError: (error: Error) =>
      setTurns((current) =>
        current.map((turn, index) =>
          index === current.length - 1 ? { ...turn, error: error.message } : turn,
        ),
      ),
  });

  // Keep the newest answer in view without yanking the page around.
  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, open]);

  // Escape is how people expect to leave a panel like this.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!status.data?.enabled) return null;

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (trimmed.length < 2 || ask.isPending) return;
    setQuestion('');
    ask.mutate(trimmed);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
          open
            ? 'bg-primary-500 text-white'
            : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
        }`}
      >
        <Sparkles className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Assistant</span>
      </button>

      {open && (
        <div
          className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-border bg-card shadow-2xl"
          role="dialog"
          aria-label="Assistant"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <h2 className="flex items-center gap-1.5 font-heading text-lg">
                <Sparkles className="h-4 w-4 text-primary-500" aria-hidden="true" />
                Assistant
              </h2>
              <p className="text-xs text-muted-foreground">
                Answers from your own data, and only what your account can see.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1.5 hover:bg-muted"
              aria-label="Close"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto p-4">
            {turns.length === 0 && (
              <div>
                <p className="text-sm text-muted-foreground">Try one of these:</p>
                <ul className="mt-2 space-y-1.5">
                  {(status.data.suggestions ?? []).map((suggestion) => (
                    <li key={suggestion}>
                      <button
                        type="button"
                        onClick={() => submit(suggestion)}
                        className="w-full rounded-lg border border-border px-3 py-2 text-left text-sm hover:bg-muted"
                      >
                        {suggestion}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {turns.map((turn, index) => (
              <div key={index} className="space-y-2">
                <p className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-primary-500 px-3 py-2 text-sm text-white">
                  {turn.question}
                </p>

                {!turn.answer && !turn.error && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}

                {turn.error && (
                  <p className="w-fit max-w-[85%] rounded-2xl rounded-bl-sm bg-danger-50 px-3 py-2 text-sm text-danger-700">
                    {turn.error}
                  </p>
                )}

                {turn.answer && (
                  <div className="w-full rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm">
                    <p>{turn.answer.text}</p>

                    {/* A refusal is shown, never swallowed. Somebody who cannot
                        see something should know that is why, not think the
                        answer was zero. */}
                    {turn.answer.refusal && (
                      <p className="mt-1.5 text-xs italic text-muted-foreground">
                        {turn.answer.refusal}
                      </p>
                    )}

                    {turn.answer.items && turn.answer.items.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {turn.answer.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="rounded-lg bg-card px-2.5 py-1.5">
                            {item.href ? (
                              <Link
                                to={item.href}
                                onClick={() => setOpen(false)}
                                className="font-medium text-primary-600 hover:underline"
                              >
                                {item.label}
                              </Link>
                            ) : (
                              <span className="font-medium">{item.label}</span>
                            )}
                            {item.detail && (
                              <span className="block text-xs text-muted-foreground">
                                {item.detail}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/*
                      Results from outside the business, marked so they cannot
                      be mistaken for GoSpeedNet's own records: their own
                      heading, a different border, and the source on every row.
                      Somebody deciding whether to send a technician up a pole
                      has to know which is which without looking twice.
                    */}
                    {turn.answer.external && turn.answer.external.length > 0 && (
                      <div className="mt-3 rounded-lg border-l-2 border-l-accent-400 bg-card px-2.5 py-2">
                        <p className="mb-1.5 flex items-center gap-1 text-xs font-semibold label-mono tracking-[0.12em] text-accent-700">
                          <Globe className="h-3 w-3" aria-hidden="true" />
                          From outside your records
                        </p>
                        <ul className="space-y-2">
                          {turn.answer.external.map((result, resultIndex) => (
                            <li key={resultIndex}>
                              {result.url ? (
                                <a
                                  href={result.url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  className="font-medium text-primary-600 hover:underline"
                                >
                                  {result.title}
                                </a>
                              ) : (
                                <span className="font-medium">{result.title}</span>
                              )}
                              <span className="block text-xs text-muted-foreground">
                                {result.snippet}
                              </span>
                              <span className="text-[11px] text-muted-foreground/80">
                                {result.source}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <form
            className="flex gap-2 border-t border-border p-3"
            onSubmit={(event) => {
              event.preventDefault();
              submit(question);
            }}
          >
            <input
              className="input flex-1"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask about your tickets…"
              aria-label="Ask the assistant"
            />
            <button
              type="submit"
              className="btn-primary"
              disabled={question.trim().length < 2 || ask.isPending}
            >
              {ask.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" aria-hidden="true" />
              )}
              <span className="sr-only">Ask</span>
            </button>
          </form>
        </div>
      )}
    </>
  );
}
